/**
 * PartsCounterList Component
 *
 * Sortable list of parts counter persons with expandable rows.
 * Includes column headers with sort indicators.
 */

import React, { useState, useCallback } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
} from '@heroicons/react/24/outline';
import { PartsCounterPerson, SortConfig, SortField } from './types';
import { PartsCounterRow } from './PartsCounterRow';

interface PartsCounterListProps {
  persons: PartsCounterPerson[];
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

const SORT_COLUMNS: Array<{
  field: SortField;
  label: string;
  shortLabel: string;
  align: 'left' | 'right';
}> = [
  { field: 'name', label: 'Name', shortLabel: 'Name', align: 'left' },
  { field: 'grossTotal', label: 'Total Gross', shortLabel: 'Gross', align: 'right' },
  { field: 'transactionCount', label: 'Tickets', shortLabel: 'Tickets', align: 'right' },
  { field: 'lostSalesCount', label: 'Lost Sales', shortLabel: 'Lost Sale', align: 'right' },
];

export const PartsCounterList: React.FC<PartsCounterListProps> = ({
  persons,
  sortConfig,
  onSortChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((employeeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      onSortChange({
        field,
        direction:
          sortConfig.field === field && sortConfig.direction === 'desc'
            ? 'asc'
            : 'desc',
      });
    },
    [sortConfig, onSortChange]
  );

  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <BarsArrowDownIcon className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 text-green-600" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 text-green-600" />
    );
  };

  if (persons.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No parts counter data available for this period.
      </div>
    );
  }

  return (
    <div>
      {/* Sort header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="font-medium">Sort by:</span>
          {SORT_COLUMNS.map((col) => (
            <button
              key={col.field}
              onClick={() => handleSort(col.field)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                sortConfig.field === col.field
                  ? 'bg-green-100 text-green-700'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
            >
              <span>{col.shortLabel}</span>
              {renderSortIndicator(col.field)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {persons.length} person{persons.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Person rows */}
      <div className="bg-white rounded-b-lg">
        {persons.map((person, index) => (
          <PartsCounterRow
            key={person.employeeId}
            person={person}
            isExpanded={expandedIds.has(person.employeeId)}
            onToggle={() => toggleExpanded(person.employeeId)}
            rank={index + 1}
          />
        ))}
      </div>
    </div>
  );
};
